import { useEffect, useMemo, useState, Fragment } from 'react';
import axios from 'axios';
import { DateTime } from 'luxon';
import { useNavigate } from 'react-router-dom';
import Icon from '../../components/tables/Icon';
import Form from '../../components/forms/Form';
import LessonDetailModal from '../students/LessonDetailModal';
import LessonLinkChips from '../students/LessonLinkChips';
import { getLessonTopicsBulk } from '../students/workspace/lessonTopicsApi';
import { getLessonHomeworksBulk } from '../students/lessonHomeworkApi';
import { resolveTopicCourseId } from '../courses/courseLevels';
import FilterBar from '../../components/settings/FilterBar';
import IconAdd from '../../components/icons/IconAdd';
import IconTag from '../../components/icons/IconTag';
import IconPencilTool from '../../components/icons/IconPencilTool';
import language from '../../variables/language';
// Reuses the student card's lessons-list look (.student-lessons-*) instead
// of the plain generic .table styling this used to have - same rounded
// bordered wrapper, sticky header, hover rows, weekday pill and month
// separators as Events.jsx, so a group's lesson history reads the same way
// a student's does.
import '../../css/student-card.css';

// Same 'ua' -> 'uk' mapping / same month-grouping idea as the student card's
// own lessons list (Events.jsx) - kept as its own small implementation here
// (not a shared helper) since the data shape differs slightly (Lesson vs
// Event) and this is genuinely all there is to it.
const LUXON_LOCALE_BY_LANG = { en: 'en', ua: 'uk', pl: 'pl' };
const monthKey = (dateStr) => (dateStr || '').slice(0, 7);
const monthLabel = (dateStr, lang) => {
  const dt = DateTime.fromFormat(dateStr || '', 'yyyy-MM-dd').setLocale(LUXON_LOCALE_BY_LANG[lang] || 'en');
  return dt.isValid ? dt.toFormat('LLLL yyyy') : dateStr;
};

const buildLessonInputs = (groupId) => ({
  start: { name: 'start', type: 'time', tagtype: 'input', value: '', disabled: false, width: '19%' },
  end: { name: 'end', type: 'time', tagtype: 'input', value: '', disabled: false, width: '19%' },
  date: { name: 'date', type: 'date', tagtype: 'input', value: '', disabled: false, width: '19%' },
  price: { name: 'price', type: 'float', step: '0.01', tagtype: 'input', value: '', disabled: false, width: '19%' },
  duration: { name: 'duration', type: 'float', step: '0.01', tagtype: 'input', value: '', disabled: false, width: '19%' },
  groupe: { name: 'groupe', value: groupId },
});

// hideCourse=true drops the "План"/course column - used when this list
// shares a row with GroupSidebarTabs.jsx (narrower space, and the course is
// already visible from the Курси tab right next to it).
export default function GroupLessonsList({ groupId, refresh, onChange, hideCourse }) {
  const navigate = useNavigate();
  const [lessons, setLessons] = useState([]);
  const [search, setSearch] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [showAddForm, setShowAddForm] = useState(false);
  const [menuLessonId, setMenuLessonId] = useState('');
  const [modalState, setModalState] = useState('none');
  const [lessonInputs] = useState(() => buildLessonInputs(groupId));
  // keyed by lesson id - see Events.jsx's identical twin for why this is a
  // bulk fetch rather than one request per row.
  const [topicsByLesson, setTopicsByLesson] = useState({});
  const [homeworksByLesson, setHomeworksByLesson] = useState({});

  useEffect(() => {
    axios.get('/api/lessons?groupe=' + groupId, { withCredentials: true })
      .then(response => setLessons(response.data || []))
      .catch(err => { if (err.response && err.response.status === 401) navigate('/login'); });
  }, [groupId, refresh, navigate]);

  useEffect(() => {
    const lessonIds = Array.from(new Set(lessons.map(l => l.id).filter(Boolean)));
    if (lessonIds.length === 0) {
      setTopicsByLesson({});
      setHomeworksByLesson({});
      return;
    }
    getLessonTopicsBulk(lessonIds).then(response => {
      const byLesson = {};
      (Array.isArray(response.data) ? response.data : []).forEach(lt => {
        if (!lt.lesson) return;
        const key = lt.lesson.id;
        if (!byLesson[key]) byLesson[key] = [];
        byLesson[key].push(lt);
      });
      setTopicsByLesson(byLesson);
    }).catch(() => setTopicsByLesson({}));
    getLessonHomeworksBulk(lessonIds).then(response => {
      const byLesson = {};
      (Array.isArray(response.data) ? response.data : []).forEach(hw => {
        if (!hw.lesson || !hw.exerciseTemplate) return;
        const key = hw.lesson.id;
        if (!byLesson[key]) byLesson[key] = [];
        byLesson[key].push(hw);
      });
      setHomeworksByLesson(byLesson);
    }).catch(() => setHomeworksByLesson({}));
  }, [lessons]);

  // Group context: always this group's own workspace, unlike Events.jsx
  // (student) which has exactly one student to resolve to - a group lesson
  // has several participants, but the group workspace merges everyone's
  // activity into one view anyway, so there's no picker needed here.
  const openTopic = (topic) => {
    const courseId = resolveTopicCourseId(topic);
    if (!courseId) return;
    navigate('/groups/' + groupId + '/workspace/' + courseId + '?topic=' + topic.id);
  };
  // Same reasoning as Events.jsx's own openExercise - a homework exercise's
  // real place is the lesson's own topic (LessonHomeworkPanel.jsx attaches
  // it there), so land on that topic in the group workspace instead of the
  // bare exercise-template constructor.
  const openExercise = (lessonId) => {
    const topics = topicsByLesson[lessonId] || [];
    if (topics.length === 0) return;
    openTopic(topics[0].topic);
  };

  const visibleLessons = useMemo(() => {
    const query = search.trim().toLowerCase();
    return lessons.filter(lesson => {
      if (query) {
        const haystack = ((lesson.course || '') + ' ' + (lesson.homework || '')).toLowerCase();
        if (!haystack.includes(query)) return false;
      }
      if (dateFrom && lesson.date && lesson.date < dateFrom) return false;
      if (dateTo && lesson.date && lesson.date > dateTo) return false;
      return true;
    }).sort((a, b) => {
      // newest first - the backend's own /api/lessons?groupe= sort is
      // ascending (oldest first), so this reverses it client-side rather
      // than touching the shared LessonController (used by other, still
      // oldest-first callers too).
      if (!a.date && !b.date) return 0;
      if (!a.date) return 1;
      if (!b.date) return -1;
      if (a.date !== b.date) return a.date < b.date ? 1 : -1;
      return (b.start || '').localeCompare(a.start || '');
    });
  }, [lessons, search, dateFrom, dateTo]);

  const stateHundler = (state, id) => {
    if (state === 'menu') {
      setMenuLessonId(id);
      setModalState('menu');
    } else if (state === 'none') {
      setModalState('none');
    }
  };

  const onModalRefresh = () => { onChange(); };

  const lang = language[language.lang];
  const weekdayInfo = (dateStr) => {
    if (!dateStr) return null;
    const dt = DateTime.fromFormat(dateStr, 'yyyy-MM-dd');
    if (!dt.isValid) return null;
    return { label: lang.weekDaysShort[dt.weekday - 1], isWeekend: dt.weekday >= 6 };
  };
  const todayStr = DateTime.now().toFormat('yyyy-MM-dd');
  // same legacy quirk Events.jsx already guards against - some lesson text
  // columns store the literal strings "null"/"undefined" rather than a real
  // empty value.
  const cleanText = (val) => (val === 'null' || val === 'undefined' || val === null || val === undefined) ? '' : val;

  return (
    <div className="group-card-section">
      <div className="group-lessons-toolbar">
        <h4 className="group-card-section-title" style={{ margin: 0 }}>{lang.groupCard.lessonsTitle}</h4>
        <FilterBar
          search={{ value: search, onChange: setSearch, placeholder: lang.placeholderSearch }}
          filters={[
            { key: 'dateFrom', type: 'date', label: lang.dateFrom, value: dateFrom, onChange: setDateFrom },
            { key: 'dateTo', type: 'date', label: lang.dateTo, value: dateTo, onChange: setDateTo },
          ]}
          resultCount={visibleLessons.length}
        />
        <div className="group-lessons-add-btn" onClick={() => setShowAddForm(prev => !prev)}>
          <IconAdd /> {lang.groupCard.addLessonButton}
        </div>
      </div>

      {showAddForm ? (
        <div className="group-lessons-add-panel">
          <Form inputs={lessonInputs} url="/api/lessons"
            onRefreshHundler={() => { setShowAddForm(false); onChange(); }}
            name={lang.modals.lessons.createTitle} />
        </div>
      ) : ''}

      {visibleLessons.length === 0 ? (
        <p className="group-lessons-empty">{lang.groupCard.noLessons}</p>
      ) : (
        <div className="student-lessons-table-wrap">
          <table className="student-lessons-table">
            <thead>
              <tr>
                <th>{lang.date}</th>
                {hideCourse ? '' : <th>{lang.course}</th>}
                <th>{lang.topicColumn}</th>
                <th>{lang.exerciseHomeworkColumn}</th>
                <th>{lang.duration}</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {visibleLessons.map((lesson, index) => {
                const weekday = weekdayInfo(lesson.date);
                const isToday = lesson.date === todayStr;
                const isNewMonth = index === 0 || monthKey(lesson.date) !== monthKey(visibleLessons[index - 1].date);
                return (
                  <Fragment key={lesson.id}>
                    {isNewMonth ? (
                      <tr className='student-lessons-month-row'>
                        <td colSpan={hideCourse ? 5 : 6}>
                          <span className='student-lessons-month-label'>{monthLabel(lesson.date, language.lang)}</span>
                        </td>
                      </tr>
                    ) : ''}
                    <tr className={'student-lessons-row' + (isToday ? ' student-lessons-row-today' : '')}
                      onClick={() => stateHundler('menu', lesson.id)}>
                      <td className='student-lessons-date-cell'>
                        {isToday ? <span className='student-lessons-today-marker' title={lang.today}></span> : ''}
                        <span className='student-lessons-date-tag'>{lesson.date}</span>
                        <span className='student-lessons-time-tag'>{lesson.start}{lesson.end ? '–' + lesson.end : ''}</span>
                        {weekday ? <span className={'student-lessons-weekday' + (weekday.isWeekend ? ' weekend' : '')}>{weekday.label}</span> : ''}
                      </td>
                      {hideCourse ? '' : <td className='student-lessons-text-cell'>{cleanText(lesson.course)}</td>}
                      <td className='student-lessons-text-cell'>
                        <LessonLinkChips icon={IconTag} items={
                          (topicsByLesson[lesson.id] || []).map(lt => ({
                            id: lt.id, label: lt.topic.name, onClick: () => openTopic(lt.topic),
                          }))
                        } />
                      </td>
                      <td className='student-lessons-text-cell'>
                        <LessonLinkChips icon={IconPencilTool} items={
                          (homeworksByLesson[lesson.id] || []).map(hw => ({
                            id: hw.id, label: hw.exerciseTemplate.name, onClick: () => openExercise(lesson.id),
                          }))
                        } />
                      </td>
                      <td>{lesson.duration}</td>
                      <td className='student-lessons-actions-cell' onClick={e => e.stopPropagation()}>
                        <div className='student-lessons-icon-btn'>
                          <Icon mode="delete" url={'/api/lessons/' + lesson.id} onRefreshHundler={onChange}
                            className="icon-delete" classes="icon-dimensions-big" />
                        </div>
                      </td>
                    </tr>
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <LessonDetailModal isOpen={modalState === 'menu'} lessonId={menuLessonId}
        onClose={() => stateHundler('none')} onSaved={onModalRefresh}
        onParticipantsChanged={onModalRefresh} />
    </div>
  );
}
